import { useState } from "react";
import { motion } from "framer-motion";
import { Truck, Lock, Mail, Loader2 } from "lucide-react";
import { login, setToken, setUser } from "../lib/api";

const HERO = "https://images.unsplash.com/photo-1592805144716-feeccccef5ac?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwxfHxzZW1pJTIwdHJ1Y2slMjBoaWdod2F5JTIwZHJpdmVyJTIwYXNwaGFsdCUyMGRhcmslMjByb2FkfGVufDB8fHx8MTc5MjI0NzY4NHww&ixlib=rb-4.1.0&q=85";

function errorText(detail) {
  if (!detail) return "Login failed. Try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export const LoginForm = ({ onAuthed }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await login(email.trim(), password);
      setToken(data.token);
      setUser(data.user);
      onAuthed(data.user);
    } catch (err) {
      setError(errorText(err?.response?.data?.detail) || "Incorrect email or password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden">
      <div
        className="absolute inset-0 opacity-25"
        style={{ backgroundImage: `url(${HERO})`, backgroundSize: "cover", backgroundPosition: "center" }}
      />
      <div className="absolute inset-0 bg-gradient-to-t from-[#0B0C0E] via-[#0B0C0E]/80 to-[#0B0C0E]/40" />
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative w-full max-w-md p-8 rounded-lg bg-[#13161C] border border-[#262C38] shadow-2xl"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="h-11 w-11 rounded bg-[#F95722] flex items-center justify-center">
            <Truck className="h-6 w-6 text-white" />
          </div>
          <div>
            <div className="font-heading text-2xl font-extrabold uppercase tracking-tight text-white leading-none">
              Hylas
            </div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-slate-400">
              Matrix
            </div>
          </div>
        </div>
        <p className="text-sm text-slate-300 mb-6 leading-relaxed">
          Sign in to your recruiter account to call the hottest drivers first.
        </p>
        <form onSubmit={submit} className="space-y-4">
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            <input
              data-testid="login-email-input"
              type="email"
              autoComplete="username"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@hylascompanion.us"
              className="w-full bg-[#0B0C0E] border border-[#262C38] rounded px-10 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[#F95722] transition-colors"
            />
          </div>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
            <input
              data-testid="login-password-input"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Password"
              className="w-full bg-[#0B0C0E] border border-[#262C38] rounded px-10 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[#F95722] transition-colors"
            />
          </div>
          {error && (
            <p data-testid="login-error-msg" className="text-xs text-red-400 font-mono">
              {error}
            </p>
          )}
          <button
            data-testid="login-submit-btn"
            type="submit"
            disabled={loading || !email.trim() || !password}
            className="w-full inline-flex items-center justify-center gap-2 bg-[#F95722] hover:bg-[#FF6B38] disabled:opacity-50 text-white font-bold py-3 rounded text-sm uppercase tracking-wide transition-transform active:scale-95"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Sign in
          </button>
        </form>
      </motion.div>
    </div>
  );
};
